import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  getDoc,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from './config';
import { logActivity, ACTIONS, ENTITY_TYPES } from './activityLogService';

const RECEIVABLES_COLLECTION = 'tradeReceivables';
const CREDITORS_COLLECTION = 'creditors';

/**
 * Get all trade receivables — reads from creditors collection (live data source)
 */
export const getTradeReceivables = async (filters = {}) => {
  try {
    const q = query(collection(db, CREDITORS_COLLECTION), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);

    const receivables = snapshot.docs.map(d => {
      const data = d.data();
      const balance = data.balance || 0;
      return {
        id: d.id,
        customerId: data.customerId || '',
        customerName: data.customerName || '',
        customerPhone: data.customerPhone || '',
        customerEmail: data.customerEmail || '',
        customerType: data.customerType || '',
        invoiceNumber: data.bills?.length ? `${data.bills.length} invoice(s)` : '—',
        totalAmount: data.totalCredited || 0,
        paidAmount: data.totalPaid || 0,
        balanceAmount: balance,
        status: data.status === 'Settled' ? 'paid'
          : data.status === 'Overdue' ? 'overdue'
          : balance > 0 ? 'pending' : 'paid',
        paymentType: data.category === 'Koko' ? 'koko'
          : data.category === 'MintPay' ? 'mintpay'
          : data.category === 'COD' ? 'cod' : 'credit',
        category: data.category || 'Credit',
        dueDate: data.dueDate ? { toDate: () => new Date(data.dueDate) } : null,
        bills: data.bills || [],
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
      };
    });

    let filtered = receivables;
    if (filters.status) filtered = filtered.filter(r => r.status === filters.status);
    if (filters.customerId) filtered = filtered.filter(r => r.customerId === filters.customerId);

    return { success: true, data: filtered };
  } catch (error) {
    console.error('Error getting trade receivables:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Create a new trade receivable
 */
export const createTradeReceivable = async (receivableData, currentUser) => {
  try {
    const receivableRef = await addDoc(collection(db, RECEIVABLES_COLLECTION), {
      customerId: receivableData.customerId,
      customerName: receivableData.customerName,
      customerPhone: receivableData.customerPhone || '',
      customerEmail: receivableData.customerEmail || '',
      customerType: receivableData.customerType,
      invoiceId: receivableData.invoiceId,
      invoiceNumber: receivableData.invoiceNumber,
      totalAmount: receivableData.totalAmount,
      paidAmount: 0,
      balanceAmount: receivableData.totalAmount,
      status: 'pending',
      paymentType: receivableData.paymentType, // 'credit' or 'cod'
      category: receivableData.category || 'Regular', // 'Koko' or 'Regular'
      dueDate: receivableData.dueDate,
      creditTerms: receivableData.creditTerms || '7 days',
      payments: [],
      bills: receivableData.bills || [],
      settledBills: receivableData.settledBills || [],
      invoiceDate: receivableData.invoiceDate || Timestamp.now(),
      createdAt: Timestamp.now(),
      createdBy: currentUser?.uid || 'system',
      updatedAt: Timestamp.now()
    });

    // Update customer financials
    await updateCustomerBalance(receivableData.customerId);

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: ACTIONS.CREATED_CREDITOR,
      entityType: ENTITY_TYPES.CREDITOR,
      entityId: receivableRef.id,
      entityName: `Receivable - ${receivableData.customerName}`,
      details: { customerName: receivableData.customerName, amount: receivableData.totalAmount }
    });

    return { success: true, id: receivableRef.id };
  } catch (error) {
    console.error('Error creating trade receivable:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get receivable by ID
 */
export const getReceivableById = async (receivableId) => {
  try {
    const receivableDoc = await getDoc(doc(db, RECEIVABLES_COLLECTION, receivableId));
    
    if (!receivableDoc.exists()) {
      return { success: false, error: 'Receivable not found' };
    }

    return { 
      success: true, 
      data: { id: receivableDoc.id, ...receivableDoc.data() } 
    };
  } catch (error) {
    console.error('Error getting receivable:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get receivables by invoice ID
 */
export const getReceivableByInvoiceId = async (invoiceId) => {
  try {
    const q = query(
      collection(db, RECEIVABLES_COLLECTION),
      where('invoiceId', '==', invoiceId)
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return { success: false, error: 'Receivable not found' };
    }

    const receivable = {
      id: snapshot.docs[0].id,
      ...snapshot.docs[0].data()
    };

    return { success: true, data: receivable };
  } catch (error) {
    console.error('Error getting receivable by invoice:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Record payment for a receivable
 */
export const recordPayment = async (receivableId, paymentData, currentUser) => {
  try {
    const receivableRef = doc(db, RECEIVABLES_COLLECTION, receivableId);
    const receivableDoc = await getDoc(receivableRef);
    
    if (!receivableDoc.exists()) {
      return { success: false, error: 'Receivable not found' };
    }

    const receivableInfo = receivableDoc.data();
    const currentPayments = receivableInfo.payments || [];
    
    // Add new payment
    const newPayment = {
      id: `payment-${Date.now()}`,
      amount: parseFloat(paymentData.amount),
      date: paymentData.date || Timestamp.now(),
      paymentMethod: paymentData.paymentMethod,
      accountType: paymentData.accountType,
      reference: paymentData.reference || '',
      notes: paymentData.notes || '',
      receivedBy: currentUser?.uid || 'system'
    };
    
    currentPayments.push(newPayment);
    
    // Calculate new totals
    const newPaidAmount = currentPayments.reduce((sum, p) => sum + p.amount, 0);
    const newBalanceAmount = receivableInfo.totalAmount - newPaidAmount;
    
    // Determine new status
    let newStatus = 'pending';
    if (newBalanceAmount <= 0) {
      newStatus = 'paid';
    } else if (newPaidAmount > 0) {
      newStatus = 'partially_paid';
    }
    
    // Check if overdue
    if (newStatus !== 'paid' && receivableInfo.dueDate) {
      const dueDate = receivableInfo.dueDate.toDate();
      if (dueDate < new Date()) {
        newStatus = 'overdue';
      }
    }

    await updateDoc(receivableRef, {
      payments: currentPayments,
      paidAmount: newPaidAmount,
      balanceAmount: newBalanceAmount,
      status: newStatus,
      updatedAt: Timestamp.now()
    });

    // Update customer financials
    await updateCustomerBalance(receivableInfo.customerId);

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: ACTIONS.RECORDED_PAYMENT,
      entityType: ENTITY_TYPES.TRADE_RECEIVABLE,
      entityId: receivableId,
      entityName: `Payment - ${receivableInfo.customerName}`,
      details: { customerName: receivableInfo.customerName, amount: paymentData.amount, paymentMethod: paymentData.paymentMethod }
    });

    return { success: true, payment: newPayment };
  } catch (error) {
    console.error('Error recording payment:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update customer balance based on all receivables
 */
const updateCustomerBalance = async (customerId) => {
  try {
    const receivablesQuery = query(
      collection(db, RECEIVABLES_COLLECTION),
      where('customerId', '==', customerId)
    );
    
    const snapshot = await getDocs(receivablesQuery);
    
    let totalPurchased = 0;
    let totalPaid = 0;
    
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      totalPurchased += data.totalAmount || 0;
      totalPaid += data.paidAmount || 0;
    });
    
    const currentBalance = totalPurchased - totalPaid;
    
    // Update customer document
    const customerRef = doc(db, 'customers', customerId);
    await updateDoc(customerRef, {
      totalPurchased,
      totalPaid,
      currentBalance,
      updatedAt: Timestamp.now()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating customer balance:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get overdue receivables
 */
export const getOverdueReceivables = async () => {
  try {
    const receivablesSnapshot = await getDocs(collection(db, RECEIVABLES_COLLECTION));
    const now = new Date();
    
    const overdueReceivables = receivablesSnapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(receivable => {
        if (receivable.status === 'paid') return false;
        if (!receivable.dueDate) return false;
        
        const dueDate = receivable.dueDate.toDate();
        return dueDate < now;
      });

    return { success: true, data: overdueReceivables };
  } catch (error) {
    console.error('Error getting overdue receivables:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Get customer balance
 */
export const getCustomerBalance = async (customerId) => {
  try {
    const receivablesQuery = query(
      collection(db, RECEIVABLES_COLLECTION),
      where('customerId', '==', customerId)
    );
    
    const snapshot = await getDocs(receivablesQuery);
    
    let totalAmount = 0;
    let paidAmount = 0;
    let balanceAmount = 0;
    let pendingCount = 0;
    
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      totalAmount += data.totalAmount || 0;
      paidAmount += data.paidAmount || 0;
      balanceAmount += data.balanceAmount || 0;
      
      if (data.status !== 'paid') {
        pendingCount++;
      }
    });

    return {
      success: true,
      data: {
        totalAmount,
        paidAmount,
        balanceAmount,
        pendingCount,
        totalReceivables: snapshot.size
      }
    };
  } catch (error) {
    console.error('Error getting customer balance:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete receivable
 */
export const deleteReceivable = async (receivableId, currentUser) => {
  try {
    const receivableRef = doc(db, RECEIVABLES_COLLECTION, receivableId);
    const receivableDoc = await getDoc(receivableRef);
    
    if (!receivableDoc.exists()) {
      return { success: false, error: 'Receivable not found' };
    }

    const receivableData = receivableDoc.data();
    
    // Check if any payments have been made
    if (receivableData.paidAmount > 0) {
      return { 
        success: false, 
        error: 'Cannot delete receivable with recorded payments' 
      };
    }

    await deleteDoc(receivableRef);

    // Update customer financials
    await updateCustomerBalance(receivableData.customerId);

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: ACTIONS.DELETED_CREDITOR,
      entityType: ENTITY_TYPES.CREDITOR,
      entityId: receivableId,
      entityName: `Receivable - ${receivableData.customerName}`,
      details: { customerName: receivableData.customerName }
    });

    return { success: true };
  } catch (error) {
    console.error('Error deleting receivable:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get receivables summary
 */
export const getReceivablesSummary = async () => {
  try {
    const snapshot = await getDocs(collection(db, CREDITORS_COLLECTION));
    let totalOutstanding = 0, totalOverdue = 0, dueThisWeek = 0, pendingCount = 0;
    const now = new Date();
    const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    snapshot.docs.forEach(d => {
      const data = d.data();
      const balance = data.balance || 0;
      if (data.status !== 'Settled' && balance > 0) {
        totalOutstanding += balance;
        pendingCount++;
        if (data.dueDate) {
          const due = new Date(data.dueDate);
          if (due < now) totalOverdue += balance;
          else if (due <= weekFromNow) dueThisWeek += balance;
        }
      }
    });

    return { success: true, data: { totalOutstanding, totalOverdue, dueThisWeek, pendingCount } };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Update credit limit for customer
 */
export const updateCreditLimit = async (customerId, creditLimit, currentUser) => {
  try {
    const customerRef = doc(db, 'customers', customerId);
    
    await updateDoc(customerRef, {
      creditLimit: parseFloat(creditLimit),
      updatedAt: Timestamp.now()
    });

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: ACTIONS.UPDATED_CREDITOR,
      entityType: ENTITY_TYPES.CREDITOR,
      entityId: customerId,
      entityName: `Credit Limit Update`,
      details: { customerId, creditLimit }
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating credit limit:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Check if customer has exceeded credit limit
 */
export const checkCreditLimit = async (customerId, newAmount) => {
  try {
    const customerDoc = await getDoc(doc(db, 'customers', customerId));
    
    if (!customerDoc.exists()) {
      return { success: false, error: 'Customer not found' };
    }

    const customerData = customerDoc.data();
    const creditLimit = customerData.creditLimit || 0;
    const currentBalance = customerData.currentBalance || 0;
    
    if (creditLimit === 0) {
      return { success: true, withinLimit: true, message: 'No credit limit set' };
    }

    const totalBalance = currentBalance + parseFloat(newAmount);
    
    if (totalBalance > creditLimit) {
      return {
        success: true,
        withinLimit: false,
        message: `Credit limit exceeded. Limit: LKR ${creditLimit.toLocaleString()}, Current: LKR ${currentBalance.toLocaleString()}, New Total: LKR ${totalBalance.toLocaleString()}`,
        creditLimit,
        currentBalance,
        newTotal: totalBalance
      };
    }

    return {
      success: true,
      withinLimit: true,
      message: 'Within credit limit',
      creditLimit,
      currentBalance,
      newTotal: totalBalance
    };
  } catch (error) {
    console.error('Error checking credit limit:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send payment reminder (placeholder for future SMS/Email integration)
 */
export const sendPaymentReminder = async (receivableId, currentUser) => {
  try {
    const receivableDoc = await getDoc(doc(db, RECEIVABLES_COLLECTION, receivableId));
    
    if (!receivableDoc.exists()) {
      return { success: false, error: 'Receivable not found' };
    }

    const receivableData = receivableDoc.data();

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: 'payment_reminder',
      entityType: ENTITY_TYPES.CREDITOR,
      entityId: receivableId,
      entityName: `Reminder - ${receivableData.customerName}`,
      details: { customerName: receivableData.customerName, balanceAmount: receivableData.balanceAmount }
    });

    // TODO: Implement SMS/Email sending logic here
    
    return { 
      success: true, 
      message: 'Payment reminder sent successfully' 
    };
  } catch (error) {
    console.error('Error sending payment reminder:', error);
    return { success: false, error: error.message };
  }
};
